import React from 'react';
import { Colors } from '@common/tokens';
import { Link, ErrorHandler, Icon } from '@common/components';
import { useQuery } from '@common/hooks';
import {
  PENDING_APPROVAL_CHAINS_QUERY,
  PendingApprovalChainsQueryResponse,
} from '@common/graphql';
import { useCurrentMembership } from '@identity/hooks';

const TYPE_KEYS = {
  STAFFING_REQUEST: {
    TITLE: 'Staffing Request',
    DB_KEY: 'StaffingRequest',
    ROUTE_KEY: 'staffing_requests',
  },
  EXTENSION_OFFER: {
    TITLE: 'Extension Offer',
    DB_KEY: 'ExtensionOffer',
    ROUTE_KEY: 'team',
  },
} as const;

export const AppHeaderPendingApprovalItems = ({
  onVisibilityChange,
}: {
  onVisibilityChange: (visible: boolean) => void;
}) => {
  const { canShowAdminApprovalsItem } = useCurrentMembership();

  const { loading, data, error } = useQuery<PendingApprovalChainsQueryResponse>(
    PENDING_APPROVAL_CHAINS_QUERY,
    {
      skip: !canShowAdminApprovalsItem,
      fetchPolicy: 'cache-first',
    }
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="AppHeaderPendingApprovalItems"
        messageType="contextual"
      />
    );
  }

  if (loading || !data) {
    return <></>;
  }

  const pendingChains = data.approvalChainsPending;

  const isVisible = pendingChains && pendingChains.length > 0;

  // FIXME: should be in a effect
  onVisibilityChange(isVisible);

  if (!isVisible) {
    return <></>;
  }

  const filterPendingChainsByType = (
    typeKey: 'ExtensionOffer' | 'StaffingRequest'
  ) => {
    return pendingChains.filter((chain) => {
      return chain.approvable.__typename === typeKey;
    });
  };

  const pendingStaffingRequestChains = filterPendingChainsByType(
    TYPE_KEYS.STAFFING_REQUEST.DB_KEY
  );
  const pendingExtensionOfferChains = filterPendingChainsByType(
    TYPE_KEYS.EXTENSION_OFFER.DB_KEY
  );

  const getLinkByType = (
    title: string,
    routeKey: string,
    {
      length,
    }: {
      length: number;
    }
  ) => {
    return (
      length > 0 && (
        <Link to={`/works/${routeKey}`} className="text-heavy">
          {`${length} ${title}${length > 1 ? 's' : ''}`}
        </Link>
      )
    );
  };

  const staffingRequestsLink = getLinkByType(
    TYPE_KEYS.STAFFING_REQUEST.TITLE,
    TYPE_KEYS.STAFFING_REQUEST.ROUTE_KEY,
    pendingStaffingRequestChains
  );
  const extensionOffersLink = getLinkByType(
    TYPE_KEYS.EXTENSION_OFFER.TITLE,
    TYPE_KEYS.EXTENSION_OFFER.ROUTE_KEY,
    pendingExtensionOfferChains
  );

  const haveBothTypes = staffingRequestsLink && extensionOffersLink;
  const delimiter = haveBothTypes ? 'and' : '';

  const isPlural = pendingChains.length > 1;
  const toBe = isPlural ? 'are' : 'is';

  return (
    <>
      <section className="AppHeaderPendingApprovalItems">
        <Icon
          icon="exclamationTriangle"
          kind="solid"
          color="harvestGold"
          size="md"
        />
        <span className="AppHeaderPendingApprovalItems--message">
          {staffingRequestsLink} {delimiter} {extensionOffersLink} {toBe}{' '}
          pending your approval.
        </span>
      </section>
      <style jsx>
        {`
          .AppHeaderPendingApprovalItems {
            font-size: 13px;
            height: 42px;
            padding: 12px 24px;
            background-color: ${Colors.harvestGoldHyperLight};
            box-shadow: inset 4px 0px 0px ${Colors.harvestGold};
          }
          .AppHeaderPendingApprovalItems--message {
            margin-left: 8px;
          }
        `}
      </style>
    </>
  );
};
